import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { PageHeader, StatCard, Modal, ConfirmDialog, EmptyState, Spinner, Field } from '../components/ui';
import {
  HiOutlineUsers, HiOutlineSearch, HiOutlinePencil, HiOutlineTrash,
  HiOutlineFilter, HiOutlineEye
} from 'react-icons/hi';
import { format } from 'date-fns';
import { useEmployeeVisibility } from '../context/EmployeeVisibilityContext';

const DEPTS = ['Research Coordinators', 'Development Team', 'Manuscript Support Team', 'Publication & Submission Team', 'Scientific Visualization Team', 'Editorial & Documentation Team'];
const STATUSES = ['present', 'absent', 'official_leave', 'casual_leave', 'on_duty', 'half_day'];
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

const emptyAdmin = {
  firstName: '', lastName: '', dateOfBirth: '', email: '', contactNumber: '',
  bloodGroup: '', residentialAddress: '', employeeId: '', department: '',
  role: '', salary: '', joiningDate: '', status: 'active',
  aadharCard: null, passportPhoto: null
};

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState(emptyAdmin);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const { isVisible, toggleEmployee, setAllEnabled, enabledEmployeeIds } = useEmployeeVisibility();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/employees', {
        params: { search, department: filterDept, status: filterStatus, page, limit: 15 }
      });
      setEmployees(data.data);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, [search, filterDept, filterStatus, page]);

  useEffect(() => { fetch(); }, [fetch]);

  const openEdit = emp => {
    setEditModal(emp);
    setEditForm({
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      dateOfBirth: emp.dateOfBirth ? emp.dateOfBirth.split('T')[0] : '',
      email: emp.email || '',
      contactNumber: emp.contactNumber || '',
      bloodGroup: emp.bloodGroup || '',
      residentialAddress: emp.residentialAddress || '',
      employeeId: emp.employeeId || '',
      department: emp.department || '',
      role: emp.role || '',
      salary: emp.salary || '',
      joiningDate: emp.joiningDate ? emp.joiningDate.split('T')[0] : '',
      status: emp.status || 'active',
      aadharCard: null,
      passportPhoto: null,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(editForm).forEach(key => {
        if (editForm[key] !== null) {
          formData.append(key, editForm[key]);
        }
      });
      await api.put(`/employees/${editModal._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Employee updated');
      setEditModal(null);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/employees/${deleteTarget._id}`);
      toast.success('Employee removed');
      setDeleteTarget(null);
      fetch();
    } catch { toast.error('Delete failed'); }
  };

  const activeCount = employees.filter(e => e.status === 'active').length;

  return (
    <div>
      <PageHeader
        title="Employees"
        subtitle={`${total} personnel on record`}
      // action={
      //   <button className="btn-ghost flex items-center gap-2 text-sm py-2 px-4">
      //     <HiOutlineUserAdd className="w-4 h-4" />
      //     Export
      //   </button>
      // }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total" value={total} icon={HiOutlineUsers} accent="gold" />
        <StatCard label="Active" value={employees.filter(e => e.status === 'active').length} icon={HiOutlineUsers} accent="green" />
        <StatCard label="Inactive" value={employees.filter(e => e.status === 'inactive').length} icon={HiOutlineUsers} accent="red" />
        <StatCard label="Departments" value={new Set(employees.map(e => e.department).filter(Boolean)).size} icon={HiOutlineFilter} accent="blue" />
      </div>

      {/* Filters */}
      <div className="luxury-card p-4 mb-6 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="luxury-input !pl-9" placeholder="Search by name, email, ID..." />
        </div>
        <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(1); }}
          className="luxury-input w-auto min-w-[160px]">
          <option value="">All Departments</option>
          {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          className="luxury-input w-auto min-w-[140px]">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="luxury-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : employees.length === 0 ? (
          <EmptyState icon={HiOutlineUsers} title="No employees found" subtitle="Try adjusting the filters or register new employees." />
        ) : (
          <div className="overflow-x-auto">
            <table className="luxury-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>ID</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Salary</th>
                  <th>Joined</th>
                  <th className="!w-12 !text-center" title="Toggle visibility in dropdowns (Attendance / Payroll / CL Registry)">Payroll</th>
                  <th className="!text-right !pr-8">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...employees]
                  .sort((a, b) => Number(a.employeeId) - Number(b.employeeId))
                  .map(emp => {
                    const empId = emp.employeeId;
                    const checked = isVisible(empId);
                    return (
                    <tr key={emp._id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-obsidian-600 border border-gold-500/20">
                            {emp.passportPhoto ? (
                              <img
                                src={`${UPLOADS_URL}/employees/${emp.passportPhoto}`}
                                className="object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gold-400 text-sm">
                                {emp.firstName?.[0]}{emp.lastName?.[0]}
                              </div>
                            )}

                          </div>
                          <div>
                            <div className="text-slate-700 font-medium">{emp.firstName} {emp.lastName}</div>
                            <div className="text-slate-500 text-xs">{emp.email}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className=" text-gold-500 text-xs">{emp.employeeId || '—'}</span></td>
                      <td><span className="text-slate-600">{emp.department || '—'}</span></td>
                      <td><span className="text-slate-600">{emp.role || '—'}</span></td>
                      <td>
                        <span className={emp.status === 'active' ? 'badge-active' : 'badge-inactive'}>
                          {emp.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <span className=" text-slate-600 text-xs">
                          {emp.salary ? `₹ ${Number(emp.salary).toLocaleString('en-IN')}` : '—'}
                        </span>
                      </td>
                      <td>
                        <span className="text-slate-500 text-xs">
                          {emp.joiningDate ? format(new Date(emp.joiningDate), 'dd MMM yyyy') : '—'}
                        </span>
                      </td>
                      <td className="!text-center !w-12">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEmployee(empId)}
                          className="w-4 h-4 rounded accent-gold-500 cursor-pointer"
                          title={checked ? 'Hide from dropdowns' : 'Show in dropdowns'}
                        />
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setViewModal(emp)}
                            className="p-2 text-slate-400 hover:text-sapphire-400 hover:bg-sapphire-500/10 rounded-lg transition-all">
                            <HiOutlineEye className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEdit(emp)}
                            className="p-2 text-slate-400 hover:text-gold-400 hover:bg-gold-500/10 rounded-lg transition-all">
                            <HiOutlinePencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteTarget(emp)}
                            className="p-2 text-slate-400 hover:text-crimson-400 hover:bg-crimson-500/10 rounded-lg transition-all">
                            <HiOutlineTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gold-500/10">
            <span className="text-slate-400 text-xs ">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-ghost text-xs py-1.5 px-3 disabled:opacity-40">Prev</button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="btn-ghost text-xs py-1.5 px-3 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      <Modal open={!!viewModal} onClose={() => setViewModal(null)} title="Employee Details" size="lg">
        {viewModal && (
          <div className="space-y-6">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-obsidian-600 border border-gold-500/20">
                {viewModal.passportPhoto
                  ? <img src={`${UPLOADS_URL}/employees/${viewModal.passportPhoto}`} alt="" className="object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-gold-400 text-2xl">
                    {viewModal.firstName?.[0]}{viewModal.lastName?.[0]}
                  </div>
                }
              </div>
              <div>
                <h2 className="text-2xl text-slate-700">{viewModal.firstName} {viewModal.lastName}</h2>
                <div className=" text-gold-400 text-sm">{viewModal.employeeId || 'ID Pending'}</div>
                <span className={viewModal.status === 'active' ? 'badge-active' : 'badge-inactive'}>{viewModal.status || 'active'}</span>
              </div>
            </div>
            <div className="gold-line" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                ['Email', viewModal.email], ['Contact', viewModal.contactNumber],
                ['Date of Birth', viewModal.dateOfBirth ? format(new Date(viewModal.dateOfBirth), 'dd MMM yyyy') : '—'],
                ['Blood Group', viewModal.bloodGroup || '—'],
                ['Department', viewModal.department || '—'], ['Role', viewModal.role || '—'],
                ['Salary', viewModal.salary ? `₹ ${Number(viewModal.salary).toLocaleString('en-IN')}` : '—'],
                ['Joining Date', viewModal.joiningDate ? format(new Date(viewModal.joiningDate), 'dd MMM yyyy') : '—'],
              ].map(([k, v]) => (
                <div key={k}>
                  <span className="text-xs  text-slate-400 uppercase tracking-wider">{k}</span>
                  <div className="text-slate-800 mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            <div>
              <span className="text-xs  text-slate-400 uppercase tracking-wider">Address</span>
              <div className="text-slate-800 mt-0.5 text-sm">{viewModal.residentialAddress}</div>
            </div>
            {viewModal.aadharCard && (
              <a href={`${UPLOADS_URL}/employees/${viewModal.aadharCard}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-gold-400 text-sm hover:text-gold-300 underline underline-offset-2">
                View Aadhar Card
              </a>
            )}
            {viewModal.aadharCard && (
              <a
                href={`${process.env.REACT_APP_API_URL}/employees/download/${viewModal.aadharCard}`}
                className="text-gold-400 text-sm hover:text-gold-300 underline ml-4"
              >
                Download Aadhar Card
              </a>
            )}
            {viewModal.passportPhoto && (
              <a
                href={`${process.env.REACT_APP_API_URL}/employees/download/${viewModal.passportPhoto}`}
                className="text-gold-400 text-sm hover:text-gold-300 underline ml-4"
              >
                Download Passport Photo
              </a>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title="Edit Employee" size="lg">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <h4 className="col-span-full text-xs font-semibold text-slate-400 uppercase tracking-widest border-b border-gold-500/10 pb-2">Personal Information</h4>
            <Field label="First Name">
              <input value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })}
                className="luxury-input" placeholder="John" />
            </Field>
            <Field label="Last Name">
              <input value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })}
                className="luxury-input" placeholder="Doe" />
            </Field>
            <Field label="Email Address">
              <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                className="luxury-input" placeholder="john.doe@example.com" />
            </Field>
            <Field label="Contact Number">
              <input value={editForm.contactNumber} onChange={e => setEditForm({ ...editForm, contactNumber: e.target.value })}
                className="luxury-input" placeholder="+91 98765 43210" />
            </Field>
            <Field label="Date of Birth">
              <input type="date" value={editForm.dateOfBirth} onChange={e => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
                className="luxury-input" />
            </Field>
            <Field label="Blood Group">
              <input value={editForm.bloodGroup} onChange={e => setEditForm({ ...editForm, bloodGroup: e.target.value })}
                className="luxury-input" placeholder="O+" />
            </Field>
            <div className="col-span-full">
              <Field label="Residential Address">
                <textarea value={editForm.residentialAddress} onChange={e => setEditForm({ ...editForm, residentialAddress: e.target.value })}
                  className="luxury-input min-h-[80px]" placeholder="123 Luxury Lane, Emerald City" />
              </Field>
            </div>

            <h4 className="col-span-full text-xs font-semibold text-slate-400 uppercase tracking-widest border-b border-gold-500/10 pb-2 mt-4">Administrative Details</h4>
            <Field label="Employee ID">
              <input value={editForm.employeeId} onChange={e => setEditForm({ ...editForm, employeeId: e.target.value })}
                className="luxury-input" placeholder="EMP0001" />
            </Field>
            <Field label="Department">
              <select value={editForm.department} onChange={e => setEditForm({ ...editForm, department: e.target.value })} className="luxury-input">
                <option value="">Select</option>
                {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Role">
              <input value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                className="luxury-input" placeholder="Software Engineer" />
            </Field>
            <Field label="Salary (₹)">
              <input type="number" value={editForm.salary} onChange={e => setEditForm({ ...editForm, salary: e.target.value })}
                className="luxury-input" placeholder="50000" />
            </Field>
            <Field label="Joining Date">
              <input type="date" value={editForm.joiningDate} onChange={e => setEditForm({ ...editForm, joiningDate: e.target.value })}
                className="luxury-input" />
            </Field>
            <Field label="Status">
              <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="luxury-input">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>

            <h4 className="col-span-full text-xs font-semibold text-slate-400 uppercase tracking-widest border-b border-gold-500/10 pb-2 mt-4">Document Uploads</h4>
            <Field label="Aadhar Card (Leave empty to keep existing)">
              <input type="file" onChange={e => setEditForm({ ...editForm, aadharCard: e.target.files[0] })}
                className="luxury-input py-1" accept=".pdf,.jpg,.jpeg,.png" />
            </Field>
            <Field label="Passport Photo (Leave empty to keep existing)">
              <input type="file" onChange={e => setEditForm({ ...editForm, passportPhoto: e.target.files[0] })}
                className="luxury-input py-1" accept=".jpg,.jpeg,.png" />
            </Field>
          </div>
          <div className="flex justify-end gap-3 pt-6 border-t border-gold-500/10">
            <button onClick={() => setEditModal(null)} className="btn-ghost px-6">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="btn-gold px-12">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Remove Employee"
        message={`Are you sure you want to remove ${deleteTarget?.firstName} ${deleteTarget?.lastName}? This action cannot be undone.`}
        danger
      />
    </div>
  );
}
